import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useAnalyticsFilterActions,
  useAnalyticsFilters,
  useActiveFilterTags,
} from "@/hooks/useAnalyticsFilters";
import type { CoverholderOption, DqStatusFilter, VesselTypeKey } from "@/types/analytics";

interface AnalyticsFiltersProps {
  coverholders: CoverholderOption[];
}

const TYPE_PILLS: Array<{ key: VesselTypeKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "tanker", label: "Tankers" },
  { key: "bulk", label: "Bulk carriers" },
  { key: "container", label: "Container" },
  { key: "gas", label: "Gas carriers" },
];

const DQ_PILLS: Array<{ key: DqStatusFilter; label: string }> = [
  { key: "all", label: "All" },
  { key: "pass", label: "Passed" },
  { key: "fail", label: "Failed" },
];

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "text-[11px] px-3 py-1 rounded-full border whitespace-nowrap cursor-pointer transition-all",
        active
          ? "bg-[var(--color-brand-primary)]/10 text-[var(--color-brand-primary-dark)] border-[var(--color-brand-primary)]/50 font-medium"
          : "bg-[var(--color-bg-primary)] text-[var(--color-text-secondary)] border-[var(--color-border-light)] hover:border-[var(--color-border)] hover:text-[var(--color-text-primary)]"
      )}
    >
      {children}
    </button>
  );
}

function Divider() {
  return (
    <div className="w-px h-7 bg-[var(--color-border-light)] flex-shrink-0" aria-hidden="true" />
  );
}

export function AnalyticsFilters({ coverholders }: AnalyticsFiltersProps) {
  const filters = useAnalyticsFilters();
  const actions = useAnalyticsFilterActions();
  const activeTags = useActiveFilterTags();

  const selectClass =
    "text-[11px] px-2.5 h-7 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-text-secondary)] focus:outline-none focus:border-[var(--color-brand-primary)]";

  return (
    <div className="bg-white border border-[var(--color-border-light)] rounded-xl px-5 py-3.5 flex flex-col gap-3 mb-5">
      <div className="flex gap-3 flex-wrap items-center">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wide font-medium text-[var(--color-text-tertiary)] min-w-[80px]">
            Coverholder
          </span>
          <select
            className={selectClass}
            value={filters.coverholder}
            onChange={(e) => actions.setCoverholder(e.target.value)}
            aria-label="Filter by coverholder"
          >
            {coverholders.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <Divider />

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wide font-medium text-[var(--color-text-tertiary)] min-w-[80px]">
            Date range
          </span>
          <input
            type="date"
            className={selectClass}
            value={filters.dateFrom}
            onChange={(e) => actions.setDateFrom(e.target.value)}
            aria-label="Filter date from"
          />
          <span className="text-[11px] text-[var(--color-text-tertiary)]">to</span>
          <input
            type="date"
            className={selectClass}
            value={filters.dateTo}
            onChange={(e) => actions.setDateTo(e.target.value)}
            aria-label="Filter date to"
          />
        </div>

        <Divider />

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wide font-medium text-[var(--color-text-tertiary)] min-w-[80px]">
            Vessel type
          </span>
          {TYPE_PILLS.map((p) => (
            <Pill
              key={p.key}
              active={filters.vesselType === p.key}
              onClick={() => actions.setVesselType(p.key)}
            >
              {p.label}
            </Pill>
          ))}
        </div>

        <Divider />

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wide font-medium text-[var(--color-text-tertiary)] min-w-[80px]">
            DQ status
          </span>
          {DQ_PILLS.map((p) => (
            <Pill
              key={p.key}
              active={filters.dqStatus === p.key}
              onClick={() => actions.setDqStatus(p.key)}
            >
              {p.label}
            </Pill>
          ))}
        </div>
      </div>

      {activeTags.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[11px] text-[var(--color-text-tertiary)]">Active:</span>
          {activeTags.map((tag) => (
            <span
              key={tag.key}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-[1px] rounded-full bg-[var(--color-brand-primary)]/10 text-[var(--color-brand-primary-dark)] border border-[var(--color-brand-primary)]/40 font-medium"
            >
              {tag.label}
              <button
                type="button"
                aria-label={`Clear ${tag.key}`}
                onClick={() => actions.clear(tag.key)}
                className="opacity-60 hover:opacity-100 cursor-pointer"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={actions.reset}
            className="text-[11px] text-[var(--color-text-tertiary)] underline hover:text-[var(--color-text-secondary)] cursor-pointer ml-1"
          >
            Reset all
          </button>
        </div>
      )}
    </div>
  );
}
